import { A } from '@solidjs/router'
import { type Component, createMemo, createSignal, type JSX, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Badge from '~/components/Badge'
import Icon from '~/components/Icon'
import type { DrawerItemBadgePropsType, DrawerItemPropsType } from '~/types/DrawerItem'

const normalizeBadge = (b: number | DrawerItemBadgePropsType | undefined): DrawerItemBadgePropsType | undefined => {
  if (typeof b === 'number') return { count: b }
  return b
}

const DrawerItem: Component<DrawerItemPropsType> = allProps => {
  const [props, others] = splitProps(allProps, [
    'class',
    'classList',
    'onClick',
    'title',
    'subtitle',
    'icon',
    'start',
    'end',
    'startIcon',
    'endIcon',
    'badge',
    'active',
    'disabled',
    'href',
    'to',
    'submenu',
    'open',
    'defaultOpen',
    'onOpenChange'
  ])

  const isControlled = () => props.open !== undefined
  const [internalOpen, setInternalOpen] = createSignal(props.defaultOpen || false)
  const isOpen = createMemo(() => (isControlled() ? !!props.open : internalOpen()))

  const rootClasses = () =>
    twMerge(
      'm-0 group w-full inline-flex items-center rounded-lg transition-colors select-none px-3 py-2.5',
      props.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      props.active ? 'bg-primary-soft text-on-primary-soft' : 'text-foreground-muted hover:bg-surface-hover',
      props.class
    )

  const renderBadge = () => {
    const spec = normalizeBadge(props.badge)
    if (!spec) return null
    return (
      <Badge {...spec}>
        <span class="inline-block w-4 h-4" />
      </Badge>
    )
  }

  const handleToggle = () => {
    if (props.submenu?.length) {
      if (isControlled()) props.onOpenChange?.(!isOpen())
      else setInternalOpen(o => !o)
    }
  }

  const isLink = !!(props.to || props.href)
  const Content = (
    <div
      class={rootClasses()}
      classList={props.classList}
      onClick={e => {
        if (props.disabled) return
        allProps.onClick?.(e)
        if (!isLink) handleToggle()
      }}
      {...others}
    >
      {/* Inicio: slot libre */}
      <Show when={props.start}>
        <div class="mr-2 flex items-center">{props.start}</div>
      </Show>

      {/* Icono principal en contenedor */}
      <Show when={props.icon}>
        <div class="mr-2">{typeof props.icon === 'string' || (typeof props.icon === 'object' && 'body' in props.icon!) ? <Icon icon={props.icon!} size={24} /> : (props.icon as JSX.Element)}</div>
      </Show>

      {/* startIcon pequeño adicional */}
      <Show when={props.startIcon}>
        {typeof props.startIcon === 'string' || (typeof props.startIcon === 'object' && 'body' in props.startIcon!) ? (
          <Icon icon={props.startIcon!} width={16} height={16} class="mr-2 opacity-80" />
        ) : (
          <span class="mr-2 opacity-80 inline-flex items-center">{props.startIcon as JSX.Element}</span>
        )}
      </Show>

      {/* Texto */}
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm font-medium text-foreground">{props.title ?? ''}</div>
        <Show when={props.subtitle}>
          <div class="truncate text-xs text-foreground-subtle">{props.subtitle}</div>
        </Show>
      </div>

      {/* Badge */}
      <Show when={Boolean(props.badge)}>
        <div class="ml-3">{renderBadge()}</div>
      </Show>

      {/* endIcon y slot end */}
      <Show when={props.endIcon}>
        {typeof props.endIcon === 'string' || (typeof props.endIcon === 'object' && 'body' in props.endIcon!) ? (
          <Icon icon={props.endIcon!} width={16} height={16} />
        ) : (
          <span class="inline-flex items-center">{props.endIcon as JSX.Element}</span>
        )}
      </Show>
      <Show when={props.end}>
        <div class="ml-3 flex items-center">{props.end}</div>
      </Show>
    </div>
  )

  return (
    <div class="m-0">
      {props.to ? (
        <A href={props.to} class="block">
          {Content}
        </A>
      ) : props.href ? (
        <a href={props.href} target={allProps.target} rel={allProps.target === '_blank' ? 'noopener noreferrer' : undefined} class="block">
          {Content}
        </a>
      ) : (
        Content
      )}

      {/* Submenú */}
      <Show when={props.submenu?.length && isOpen()}>
        <div class="mt-1 ml-4 space-y-1">
          {props.submenu?.map(item => {
            const classes = twMerge('flex items-center gap-3 px-3 py-2 text-sm rounded-lg', item.active ? 'bg-primary-soft text-on-primary-soft' : 'text-foreground-muted hover:bg-surface-hover')

            const Inner = (
              <>
                <Show when={item.icon}>
                  {typeof item.icon === 'string' || (typeof item.icon === 'object' && 'body' in item.icon!) ? (
                    <Icon icon={item.icon!} width={16} />
                  ) : (
                    <span class="inline-flex items-center">{item.icon as JSX.Element}</span>
                  )}
                </Show>
                <span class="flex-1 text-left truncate">{item.label}</span>
                <Show when={item.badge !== undefined}>
                  <div class="ml-2">
                    {(() => {
                      const spec = normalizeBadge(item.badge)
                      return (
                        <Badge {...(spec || {})}>
                          <span class="inline-block w-4 h-4" />
                        </Badge>
                      )
                    })()}
                  </div>
                </Show>
              </>
            )

            return item.to ? (
              <A href={item.to} class={classes}>
                {Inner}
              </A>
            ) : item.href ? (
              <a href={item.href} target={item.target} rel={item.target === '_blank' ? 'noopener noreferrer' : undefined} class={classes}>
                {Inner}
              </a>
            ) : (
              <div class={classes}>{Inner}</div>
            )
          })}
        </div>
      </Show>
    </div>
  )
}

export default DrawerItem
